/* eslint-disable fp/no-mutating-methods */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
} from '@mui/material'
import React, { ReactElement, useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCourses,
  fetchAccreditations,
  fetchClasses,
  fetchStoryboardSteps,
  selectAccreditationsByCourseID,
  selectAccreditationsStatus,
  selectClassesByCourseID,
  selectClassesStatus,
  selectStoryboardSteps,
  selectStoryboardStepsStatus,
} from 'src/v3/create/create.slice'
import {
  ConfirmIcon as CheckIcon,
  CancelIcon,
  CorrectIcon,
  LeftArrowIcon,
  PublishIcon,
} from 'src/v3/core/icons'
import { Course } from 'src/v3/core/types'

interface Props {
  course?: Course
  setStep: React.Dispatch<
    React.SetStateAction<
      'basic-info' | 'classes' | 'accreditations' | 'publishing'
    >
  >
}

interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

export default ({ course, setStep }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let courseId = course?.id || ''
  let accreditations = useAppSelector(selectAccreditationsByCourseID(courseId))
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let classes = useAppSelector(selectClassesByCourseID(courseId))
  let classesStatus = useAppSelector(selectClassesStatus)
  let storyboardSteps = useAppSelector(selectStoryboardSteps)
  let storyboardStepsStatus = useAppSelector(selectStoryboardStepsStatus)

  let [publishing, setPublishing] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    // Fetch all necessary data for validation
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
    if (storyboardStepsStatus === 'uninitialized') {
      dispatch(fetchStoryboardSteps())
    }
  }, [dispatch, accreditationsStatus, classesStatus, storyboardStepsStatus])

  let validation = useMemo((): ValidationResult => {
    if (!course) {
      return {
        isValid: false,
        errors: [],
        warnings: [],
      }
    }

    // Don't validate until all data is loaded
    if (
      accreditationsStatus !== 'fulfilled' ||
      classesStatus !== 'fulfilled' ||
      storyboardStepsStatus !== 'fulfilled'
    ) {
      return {
        isValid: false,
        errors: ['Loading course data...'],
        warnings: [],
      }
    }

    let errors: string[] = []
    let warnings: string[] = []

    // Check accreditations
    if (accreditations.length === 0) {
      errors.push('Course requires at least one accreditation')
    }

    // Check classes
    if (classes.length === 0) {
      errors.push('Course requires at least one class')
    }

    // Check storyboards for each class
    let classesWithoutStoryboard = classes.filter(
      (cls) => !storyboardSteps.some((s) => s.classID === cls.id),
    )
    if (classesWithoutStoryboard.length > 0) {
      errors.push(
        `The following classes need storyboards: ${classesWithoutStoryboard
          .map((c) => c.name)
          .join(', ')}`,
      )
    }

    // Warnings
    if (!course.imageName) {
      warnings.push('Course thumbnail not uploaded')
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  }, [
    course,
    accreditations,
    classes,
    storyboardSteps,
    accreditationsStatus,
    classesStatus,
    storyboardStepsStatus,
  ])

  let handlePublish = async () => {
    if (!course) return

    let action = course.published === 'published' ? 'unpublish' : 'publish'

    if (!validation.isValid && action === 'publish') {
      setErrorMessage('Please resolve all validation errors before publishing')
      return
    }

    setPublishing(true)
    try {
      await axios.put('/api/v2/create/publish-course', {
        id: course.id,
        newPublicationStatus: `${action}ed`,
      })

      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchCourses())
      setErrorMessage('')
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data || err?.message || 'Error updating course status',
      )
    } finally {
      setPublishing(false)
    }
  }

  let handleFinish = () => {
    navigate('/create/v4')
  }

  if (!course) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Course details are not available. Please complete the Basic Info step
          first.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('basic-info')}
          >
            Go to Basic Info
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Review & Publishing
      </Typography>

      {/* Course Summary */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Course Summary
          </Typography>

          <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Box>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">{course.name}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Display Name
              </Typography>
              <Typography variant="body1">{course.displayName}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Hours
              </Typography>
              <Typography variant="body1">{course.length}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Chip
                icon={
                  course.published === 'published' ? (
                    <CorrectIcon />
                  ) : (
                    <CancelIcon />
                  )
                }
                label={
                  course.published === 'published'
                    ? 'Published'
                    : 'Not Published'
                }
                variant={
                  course.published === 'published' ? 'filled' : 'outlined'
                }
                color={course.published === 'published' ? 'success' : 'default'}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Validation Results */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Validation Results
          </Typography>

          {validation.isValid ? (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon />
                <Typography>Course is ready to publish!</Typography>
              </Box>
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Please resolve the following issues:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validation.errors.map((error, index) => (
                  <li key={index}>
                    <Typography variant="body2">{error}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {validation.warnings.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Recommendations:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validation.warnings.map((warning, index) => (
                  <li key={index}>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Content Overview */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Content Overview
          </Typography>

          <Box>
            <Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {accreditations.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Accreditations
                </Typography>
              </Box>
            </Box>

            <Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {classes.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Classes
                </Typography>
              </Box>
            </Box>

            <Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {
                    classes.filter((cls) =>
                      storyboardSteps.some((s) => s.classID === cls.id),
                    ).length
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete Storyboards
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/create/v4')}>
            Save and Exit
          </Button>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('accreditations')}
          >
            Back to Accreditations
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={
              course.published === 'published' ? 'outlined' : 'contained'
            }
            color={course.published === 'published' ? 'warning' : 'primary'}
            startIcon={<PublishIcon />}
            onClick={handlePublish}
            disabled={
              publishing ||
              (!validation.isValid && course.published !== 'published')
            }
          >
            {publishing
              ? 'Updating...'
              : course.published === 'published'
                ? 'Unpublish Course'
                : 'Publish Course'}
          </Button>

          <Button
            variant="outlined"
            color="success"
            endIcon={<CheckIcon />}
            onClick={handleFinish}
          >
            Finish
          </Button>
        </Box>
      </Box>

      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mt: 3 }}
          onClose={() => setErrorMessage('')}
        >
          {errorMessage}
        </Alert>
      )}
    </Box>
  )
}
